import { useCallback, useEffect, useRef } from 'react';

import styled from '@emotion/styled';
import { selectSubjectPrerequisites, setSubjects } from 'store/exams-search/examsSearchSlice';
import { useAppDispatch, useAppSelector } from 'store';
import { useLazyGetSubjectsQuery, useLazyGetSubjectsForHybridQuery, Subject } from 'store/exams-search/examsSearchAPI';
import { selectPreparationCentre } from 'store/country/countrySlice';

import { ApiErrorPage, BcSelect } from '@britishcouncil/react-solas-ors3';
import { Alert, LoadingContainer } from '@britishcouncil/react-solas';

const useBcSelectPreserveSroll = (
  optionsContainerClassName: string
): [(wrapperElement: HTMLDivElement) => (() => void) | undefined, () => void] => {
  const scrollTopPosition = useRef(0);
  const isNextScrollValid = useRef(true);
  const optionsContainerRef = useRef<HTMLDivElement | null>(null);

  const wrapperRef = useCallback(
    (wrapperElement: HTMLDivElement) => {
      if (!wrapperElement) {
        return;
      }

      const optionsContainers = wrapperElement.getElementsByClassName(optionsContainerClassName);
      const optionsContainer = optionsContainers.item(0) as HTMLDivElement;
      if (!optionsContainer) {
        return;
      }

      optionsContainerRef.current = optionsContainer;

      const scrollListener = (evt: Event) => {
        if (!isNextScrollValid.current) {
          isNextScrollValid.current = true;

          return;
        }

        scrollTopPosition.current = (evt.target as HTMLDivElement).scrollTop;
      };
      optionsContainer.addEventListener('scroll', scrollListener);

      return () => {
        optionsContainer.removeEventListener('scroll', scrollListener);
      };
    },
    [optionsContainerClassName]
  );

  const handleChangePreserveScroll = useCallback(() => {
    isNextScrollValid.current = false;
    setTimeout(() => {
      if (!optionsContainerRef.current) {
        return;
      }

      optionsContainerRef.current.scrollTo(0, scrollTopPosition.current);
    });
  }, []);

  return [wrapperRef, handleChangePreserveScroll];
};

export const SubjectSelect = () => {
  const dispatch = useAppDispatch();
  const subjectPrerequisites = useAppSelector(selectSubjectPrerequisites);
  const [wrapperRef, handleChangePreserveScroll] = useBcSelectPreserveSroll('select-search__select');
  const pcInfo = useAppSelector(selectPreparationCentre);

  const [triggerSubjectQuery, { data, error, isFetching }] = useLazyGetSubjectsQuery();
  const [triggerHybridSubjectQuery, { data: hybridData, error: hybridError, isFetching: hybridIsFetching }] =
    useLazyGetSubjectsForHybridQuery();

  const isHybrid = !!pcInfo;

  useEffect(() => {
    if (subjectPrerequisites.complete) {
      if (isHybrid)
        triggerHybridSubjectQuery({
          awardingBody: subjectPrerequisites.awardingBody,
          location: subjectPrerequisites.location,
          preparationCentreId: pcInfo.preparationCentreId,
          qualificationLevel: subjectPrerequisites.qualificationLevel,
          sessionId: subjectPrerequisites.session!.sessionId,
        });
      else
        triggerSubjectQuery({
          ...subjectPrerequisites,
          sessionId: subjectPrerequisites.session!.sessionId,
        });
    }
  }, [triggerSubjectQuery, subjectPrerequisites, triggerHybridSubjectQuery, isHybrid, pcInfo]);

  const handleChange = (selected: unknown) => {
    handleChangePreserveScroll();

    const subjects = selected as string[];
    dispatch(setSubjects(subjects));
  };

  if (!subjectPrerequisites.complete) return null;

  if (error || hybridError) return <ApiErrorPage />;

  const content = (data: Subject[]) => (
    <>
      <label htmlFor="subjects-select">Subjects</label>
      <StyledMultiSelectWrapper ref={wrapperRef}>
        <BcSelect
          id="subjects-select"
          isLoading={isFetching}
          multiple={true}
          onChange={handleChange}
          options={data}
          placeholder="Select subjects"
        />
      </StyledMultiSelectWrapper>
    </>
  );

  if (isHybrid)
    return (
      <LoadingContainer loading={hybridIsFetching}>
        {hybridData && hybridData.length === 0 && <Alert type="warning">There are currently no subjects</Alert>}
        {hybridData && hybridData.length > 0 && content(hybridData)}
      </LoadingContainer>
    );

  return (
    <LoadingContainer loading={isFetching}>
      {data && data.length === 0 && <Alert type="warning">There are currently no subjects</Alert>}
      {data && data.length > 0 && content(data)}
    </LoadingContainer>
  );
};

const StyledMultiSelectWrapper = styled.div`
  & .select-search__select {
    max-height: 200px !important;
    position: relative !important;
    z-index: 10 !important;
  }
`;
